import {Injectable} from '@angular/core';

/**
 * Service for WebRTC signaling via the CRD signal server (Lambda + DynamoDB).
 *
 * Only two operations: send message and poll messages.
 * No sign-in, no host discovery — backend handles that via joinCrdSession.
 *
 * Holds the state of one stream (session, peer id, poller), so it is not a root service: each
 * CrdStreamComponent provides its own, and the screens of a host stream side by side.
 */
@Injectable()
export class SignalServerService {
  private serverUrl = '';
  private session = '';
  private myId = -1;
  private messagePoller: {cancel?: () => void} | null = null;

  configure(serverUrl: string, session: string, myPeerId: number) {
    this.serverUrl = serverUrl;
    this.session = session;
    this.myId = myPeerId;
    console.log(`[Signal] configured: session=${session} myId=${myPeerId} server=${serverUrl}`);
  }

  async signOut(): Promise<void> {
    if (this.myId <= 0) return;
    console.log(`[Signal] leave peerId=${this.myId}`);
    try {
      await fetch(
        `${this.serverUrl}/sessions/${this.session}/leave?peerId=${this.myId}`,
        {method: 'POST'}
      );
    } catch { /* ignore */ }
    this.myId = -1;
  }

  async sendMessage(toPeerId: number, data: string): Promise<void> {
    if (this.myId <= 0) return;
    const preview = data.length > 200 ? data.substring(0, 200) + '...' : data;
    console.log(`[Signal] SEND → ${toPeerId}: ${preview}`);
    await fetch(
      `${this.serverUrl}/messages/${this.session}/${toPeerId}?from=${this.myId}`,
      {method: 'POST', headers: {'Content-Type': 'text/plain'}, body: data}
    );
  }

  startPolling(onMessage: (data: string) => void): void {
    this.stopPolling();
    let polling = true;
    const poll = async () => {
      while (polling && this.myId > 0) {
        try {
          const resp = await fetch(
            `${this.serverUrl}/messages/${this.session}/${this.myId}`
          );
          if (!resp.ok) { await new Promise(r => setTimeout(r, 500)); continue; }
          const data = await resp.text();
          if (!data || data.trim().length === 0) continue;  // fetch round-trip is the natural throttle
          const preview = data.length > 200 ? data.substring(0, 200) + '...' : data;
          console.log(`[Signal] RECV ← ${preview}`);
          onMessage(data);
        } catch { await new Promise(r => setTimeout(r, 1000)); }
      }
    };
    poll();
    // Store a cancel function
    this.messagePoller = {cancel: () => { polling = false; }} as any;
  }

  stopPolling(): void {
    if (this.messagePoller) {
      this.messagePoller.cancel?.();
      this.messagePoller = null;
    }
  }

  getMyId(): number {
    return this.myId;
  }
}
